import {
  AfterContentInit,
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  ViewChild,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, NgControl, ValidationErrors } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HelperService } from '../../helper/helper.service';
import { ControlType } from '../control.type';
import { KEY_FILTER } from '../key-filter';
import { ControlService } from '../services/control.service';
import {SelectItem} from 'primeng/api';

@Component({
  selector: 'partnerhub-control',
  templateUrl: './control.component.html',
  styleUrls: ['./control.component.scss'],
  providers: [],
})
export class ControlComponent implements OnInit, OnDestroy, ControlValueAccessor, AfterContentInit, OnChanges {
  public get _errors(): ValidationErrors {
    if (this.formInstance) {
      return this.formInstance.errors;
    }

    return null;
  }

  public get _filter(): boolean {
    if (this.filter === '' || this.filter) {
      return true;
    }
    return false;
  }

  public get _horizontal(): boolean {
    if (this.horizontal === '' || this.horizontal) {
      return true;
    }
    return false;
  }

  public get invalid(): boolean {
    if (!this.controlService) {
      if (this.controlType === 'SELECT') {
        return this.formControl.touched && this._errors !== null;
      }

      return this.formControl.dirty && this.formControl.touched && this._errors !== null;
    } else {
      if (this.controlType === 'SELECT') {
        return (this.formControl.touched && this._errors !== null) || (this._errors !== null && this.formSubmitted);
      }

      return (
        (this.formControl.dirty && this.formControl.touched && this._errors !== null) ||
        (this._errors !== null && this.formSubmitted)
      );
    }
  }

  private readonly complete$ = new Subject<boolean>();

  private formInstance = null;

  @Input() public appendDropdown = 'body';

  @Input() public invalidByBackend: {
    isInvalid: boolean;
    message: string;
  } = { isInvalid: false, message: '' };

  /**
   * @Deprecated - Going to be removed
   * Use filter & filterBy instead
   */
  @Input() public compareFn = this.helper.compareWithByName;

  @Input() public controlType: ControlType = 'INPUT';

  /**
   * A property to uniquely identify a value in options
   */
  @Input() public dataKey: string;

  @Input() public dateFormat: string;

  @Input() public dataType: string;

  /**
   * @deprecated
   */
  @Input() public errors: ValidationErrors = null;

  @Input() public filter: boolean | string = false;

  @Input() public filterBy = '';

  public formControl = new FormControl();

  public formSubmitted = false;

  @Input() public groupName = '';

  @Input() public horizontal: boolean | string = false;

  @Input() public info: string;

  @Input() public label: string;

  @Input() public mask = '';

  @Input() public monthNavigator: boolean;

  @Input() public optionLabel = 'name';

  @Input() public options: Observable<any[]> | any[] |  SelectItem[] = [];

  @Input() public onChangeEvent: (...args: any) => void | null;

  /** Restricts user input based on a regular expression */
  @Input() public pKeyFilter: any;

  @Input() public placeholder = '';

  @Input() public readOnly = false;

  public required = false;
  /**
   * RadioGroup Return Option
   */
  @Input() public returnAsObject: boolean;

  /**
   * 	Identifier of the native element
   */
  @Input() public selectId: string;

  @Input() public showButtonBar = false;

  @Input() public tooltip = '';

  @Input() public tooltipPosition = 'top';

  @Input() public type: string;

  @Input() public yearNavigator: boolean;

  @Input() public yearRange: string;

  @Input() public locale: string;

  @Input() public class: string;

  @ViewChild('rangeCalendar') private rangeCalendar: any;

  @Input() public min: string;

  @Input() public max: string;
  @Input() public maxlength: string;

  @Input() public prefix: string;

  @Input() public currency: string;
  
  @Output() autoCompleteValueChange:EventEmitter<any> = new EventEmitter<any>(); 

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private readonly helper: HelperService,
    @Optional() private readonly controlService: ControlService
  ) {
    if (ngControl != null) {
      /**
       * Setting the control value accessor here
       * instead of the providers, so we dont run into
       * a circular dependency
       */
      ngControl.valueAccessor = this;
    }
  }

  ngOnChanges() {
    if (this.ngControl?.control?.validator) {
      this.required = this.ngControl.control.validator({} as AbstractControl)?.required;
    }
  }

  public ngAfterContentInit(): void {
    if (this.ngControl && this.ngControl.control) {
      this.formInstance = this.ngControl.control;

      const self = this;
      this.formInstance.markAsTouched = function () {
        self.formControl.markAsTouched();
      };

      this.formInstance.markAsDirty = function () {
        self.formControl.markAsDirty();
      };
    }
  }

  public ngOnDestroy() {
    this.complete$.next(true);

    this.complete$.complete();
  }

  private pKeyFilterOverride() {
    if (!this.pKeyFilter) {
      if (this.type === 'number') {
        this.pKeyFilter = 'num';
        return;
      }

      this.pKeyFilter = KEY_FILTER.ANYTHING;
    }
  }

  private maxLenghtOverride() {
    if (!this.maxlength) {
      //if "phone" or "localized number" or "numeric field with no max defined"
      if (['LOCALIZED_NUMBER'].includes(this.controlType)) {
        this.maxlength = '18';
        return;
      }
    }
  }

  private typeOverride() {
    //if this is number, maxlenght is ignored (HTML spec), so we force the type to text...
    if (this.type === 'number' && this.maxlength) {
      this.type = 'text';
    }
  }

  autoCompleteEmit(value){
    this.autoCompleteValueChange.emit(value);
  }

  private fieldOverrides() {
    this.pKeyFilterOverride();
    this.maxLenghtOverride();
    this.typeOverride();
  }

  public ngOnInit() {
    if (this.controlService) {
      this.controlService.formEvent$.subscribe((_) => (this.formSubmitted = true));
    }

    this.formControl.valueChanges.pipe().subscribe((value) => {
      this.onChange(value);
    });

    this.fieldOverrides();
  }

  public onChange = (_: string) => {};

  public onTouch = () => {};

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formControl.disable({ emitEvent: false });
    } else {
      this.formControl.enable();
    }
  }

  public writeValue(value: string): void {
    this.formControl.setValue(value);

    this.onChange(value);
  }

  public onDatesRangeFilterSelected() {
    if (this.formControl.value[1]) {
      // If second date is selected
      this.rangeCalendar.hideOverlay();
    }
  }

  public phoneFilter: RegExp = /^[\+0-9]$/;
  
  public phoneChange = (value: string) => {
    const filteredValue = value
      .split('')
      .filter((v, i) => {
        if (i > 0 && v === '+') {
          return false;
        }
        return true;
      })
      .join('');
    this.formControl.setValue(filteredValue);
  };

  /*public loadBundles(event: LazyLoadEvent): void {
    this.getBundles((event.first / event.rows) + 1, event.rows, event.sortField, event.sortOrder, event.globalFilter, event.filters);
  }

  public getBundles(offset = 1, rows = environment.pageIncrement, sortField = "id", sortOrder: any = false, globalFilter = null, filters = null){
    forkJoin(this.bundleService.getBundles(offset, rows, sortField, sortOrder, globalFilter, filters), 
    this.bundleService.getBundlesCount(globalFilter, filters))
    .subscribe(data => {
      this.bundles = data[0];
      this.totalResultSize = data[1];
    });
  }*/
}
